import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import DropZone from './drop-zone'
import { DocumentForm } from './document-form'
import { useDocumentsContext } from './context'
import { arrayMoveImmutable } from 'array-move'
import type { DocumentFormValues } from './types'

export type UploadFile = File & { preview: string }

const Upload = ({ probateCaseId }: { probateCaseId: string }) => {
  const [files, setFiles] = useState<UploadFile[]>([])
  const { createDocument, attachFiles, isUploading, updateDocument } =
    useDocumentsContext()

  const handleFileOrderChanged = (oldIndex: number, newIndex: number) => {
    setFiles(arrayMoveImmutable(files, oldIndex, newIndex))
  }

  const handleFilesDropped = (files: UploadFile[]) => {
    setFiles(
      files.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      )
    )
  }

  const handleCancel = () => {
    files.forEach((file) => URL.revokeObjectURL(file.preview))
    setFiles([])
  }

  const handleSubmit = async (values: DocumentFormValues) => {
    const createdDocument = await createDocument({
      ...values,
      probateCaseId: Number(probateCaseId),
      status: 'draft',
    })

    await attachFiles(createdDocument.id, files)

    await updateDocument(createdDocument.id, { status: 'review' })

    setFiles([])
  }

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview))
  }, []) /* eslint-disable-line react-hooks/exhaustive-deps */

  if (files.length === 0) {
    return <DropZone onDrop={handleFilesDropped} />
  }

  return (
    <DocumentForm
      url=""
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      isSubmitting={isUploading}
      files={files}
      onFileOrderChanged={handleFileOrderChanged}
    />
  )
}

Upload.propTypes = {
  probateCaseId: PropTypes.string.isRequired,
}

export default Upload
